import React, { useState, useCallback } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import { loginRequest } from '../../../authConfig';
import { useInvoicePermissions } from "../../../hooks/purchaseInvoices/useInvoicePermissions";
import { useResponsive } from "../../../hooks/purchaseInvoices/useResponsive";
import useInvoiceTable from '../../../hooks/purchaseInvoices/invoiceManagement/useInvoiceTable';
import InvoiceDesktopView from "./InvoiceDesktopView";
import InvoiceMobileView from "./InvoiceMobileView";
import InvoiceDetailsPopup from "./InvoiceDetailsPopup";
import EditInvoiceDialog from './EditInvoiceDialog';
import CreateGoodsReceiptDialog from './CreateGoodsReceiptDialog';
import GoodsReceiptCreationScreen from './GoodsReceiptCreationScreen';
import ConfirmationDialog from "../../../pages/purchaseInvoices/supplierInvoiceFiles/ConfirmationDialog";
import { StyledContainer, StyledCard } from "../../../styles/purchaseInvoices/pages/commonTableStyles";

function InvoiceTable({
    onOpenDrawer,
    onOpenManualDrawer,
    highlightedInvoiceId,
    onHighlightComplete
}) {
    const { canEdit, canDelete, canUpload } = useInvoicePermissions();
    const { isDesktop } = useResponsive();
    const { instance, accounts } = useMsal();
    const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
    const [activeInvoiceId, setActiveInvoiceId] = useState(null);

    const [goodsReceiptDialogOpen, setGoodsReceiptDialogOpen] = useState(false);
    const [goodsReceiptInvoice, setGoodsReceiptInvoice] = useState(null);
    const [goodsReceiptScreenOpen, setGoodsReceiptScreenOpen] = useState(false);
    const [goodsReceiptOrderData, setGoodsReceiptOrderData] = useState(null);
    const [goodsReceiptDate, setGoodsReceiptDate] = useState(null);

    // Custom hook - contains all business logic
    const hookData = useInvoiceTable({
        highlightedInvoiceId,
        onHighlightComplete
    });

    const {
        openEdit,
        setOpenEdit,
        editInvoice,
        openInvoicePopup,
        setOpenInvoicePopup,
        selectedInvoiceForPopup,
        approvalPackageForPopup,
        openDelete,
        setOpenDelete,
        deleteMutation,
        updateMutation,
        fileAsBase64,
        isLoading,
        isLoadingBackground,
        isLoadingFile,
        selectedFileName,
        setSelectedFileName,
        allocationKeyModalOpen,
        setAllocationKeyModalOpen,
        allocationKeys,
        isLoadingAllocationKeys,
        allocationKeysError,
        currentInvoiceYear,
        onSaveNotes,
        onApproveInvoice,
        onRejectInvoice,
        handleSaveEdit,
        handleReprocessAttention,
        initiateApproval,
        completeApprovalWithAllocationKey,
        isReprocessing,
        getAccountantDisplayName,
        accountantNamesCache
    } = hookData;

    const handleActionsClick = (event, invoiceId) => {
        event.stopPropagation();
        setActionMenuAnchorEl(event.currentTarget);
        setActiveInvoiceId(invoiceId);
    };

    const handleActionsClose = () => {
        setActionMenuAnchorEl(null);
    };

    const handleOpenGoodsReceiptDialog = useCallback((invoice) => {
        setGoodsReceiptInvoice(invoice);
        setGoodsReceiptDialogOpen(true);
    }, []);

    const handleCloseGoodsReceiptDialog = useCallback(() => {
        setGoodsReceiptDialogOpen(false);
        setGoodsReceiptInvoice(null);
    }, []);

    const handleGoodsReceiptDialogSubmit = useCallback(async ({ orderNumber, receiptDate, invoiceId }) => {
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0],
            });

            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_BASE_URL}/api/goods-receipt/order/${encodeURIComponent(orderNumber)}`,
                {
                    headers: { Authorization: `Bearer ${tokenResponse.accessToken}` }
                }
            );

            setGoodsReceiptOrderData(response.data.data);
            setGoodsReceiptDate(receiptDate);
            setGoodsReceiptDialogOpen(false);
            setGoodsReceiptScreenOpen(true);
        } catch (err) {
            console.error('Error fetching order data:', err);
            throw new Error(err.response?.data?.error || err.response?.data?.message || err.message || 'שגיאה בשליפת נתוני ההזמנה');
        }
    }, [instance, accounts]);

    const handleGoodsReceiptBack = useCallback(() => {
        setGoodsReceiptScreenOpen(false);
        setGoodsReceiptOrderData(null);
        setGoodsReceiptDate(null);
        setGoodsReceiptInvoice(null);
    }, []);

    const handleGoodsReceiptSuccess = useCallback((docNo) => {
        setGoodsReceiptScreenOpen(false);
        setGoodsReceiptOrderData(null);
        setGoodsReceiptDate(null);
        // Delay the check so the invoice list renders first before triggering the background check
        const invoiceId = goodsReceiptInvoice?.id;
        setGoodsReceiptInvoice(null);
        setTimeout(() => {
            hookData.handleCheckGoodsReceipt?.(invoiceId);
        }, 800);
    }, [hookData, goodsReceiptInvoice]);

    if (goodsReceiptScreenOpen && goodsReceiptOrderData) {
        return (
            <GoodsReceiptCreationScreen
                orderData={goodsReceiptOrderData}
                receiptDate={goodsReceiptDate}
                invoice={goodsReceiptInvoice}
                onBack={handleGoodsReceiptBack}
                onSuccess={handleGoodsReceiptSuccess}
            />
        );
    }

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
                <Typography variant="h6" color="textSecondary" sx={{ ml: 2 }}>
                    טוען נתונים...
                </Typography>
            </Box>
        );
    }

    return (
        <StyledContainer>
            {isLoadingBackground && (
                <Box display="flex" alignItems="center" sx={{ px: 2, py: 0.5, bgcolor: 'action.hover', borderRadius: 1, mb: 1 }}>
                    <CircularProgress size={14} sx={{ mr: 1 }} />
                    <Typography variant="caption" color="textSecondary">
                        טוען חשבוניות נוספות...
                    </Typography>
                </Box>
            )}
            <StyledCard>
                {isDesktop ? (
                    <InvoiceDesktopView
                        hookData={hookData}
                        actionMenuAnchorEl={actionMenuAnchorEl}
                        activeInvoiceId={activeInvoiceId}
                        onActionsClick={handleActionsClick}
                        onActionsClose={handleActionsClose}
                        onOpenDrawer={onOpenDrawer}
                        onOpenManualDrawer={onOpenManualDrawer}
                        canEdit={canEdit}
                        canDelete={canDelete}
                        canUpload={canUpload}
                        onCreateGoodsReceipt={handleOpenGoodsReceiptDialog}
                    />
                ) : (
                    <InvoiceMobileView
                        hookData={hookData}
                        actionMenuAnchorEl={actionMenuAnchorEl}
                        activeInvoiceId={activeInvoiceId}
                        onActionsClick={handleActionsClick}
                        onActionsClose={handleActionsClose}
                        onOpenDrawer={onOpenDrawer}
                        onOpenManualDrawer={onOpenManualDrawer}
                        canEdit={canEdit}
                        canDelete={canDelete}
                        canUpload={canUpload}
                        onCreateGoodsReceipt={handleOpenGoodsReceiptDialog}
                    />
                )}
            </StyledCard>

            <InvoiceDetailsPopup
                open={openInvoicePopup}
                onClose={() => setOpenInvoicePopup(false)}
                invoice={selectedInvoiceForPopup}
                approvalPackageData={approvalPackageForPopup}
                onSaveNotes={onSaveNotes}
                onApproveInvoice={onApproveInvoice}
                onRejectInvoice={onRejectInvoice}
                fileAsBase64={fileAsBase64}
                isLoadingFile={isLoadingFile}
                initiateApproval={initiateApproval}
                allocationKeyModalOpen={allocationKeyModalOpen}
                setAllocationKeyModalOpen={setAllocationKeyModalOpen}
                allocationKeys={allocationKeys}
                isLoadingAllocationKeys={isLoadingAllocationKeys}
                allocationKeysError={allocationKeysError}
                currentInvoiceYear={currentInvoiceYear}
                completeApprovalWithAllocationKey={completeApprovalWithAllocationKey}
                getAccountantDisplayName={getAccountantDisplayName}
                accountantNamesCache={accountantNamesCache}
            />

            <EditInvoiceDialog
                open={openEdit}
                onClose={() => setOpenEdit(false)}
                invoice={editInvoice}
                onSave={handleSaveEdit}
                onReprocessAttention={handleReprocessAttention}
                isSaving={updateMutation.isPending || isReprocessing}
            />

            <ConfirmationDialog
                selectedFileName={selectedFileName}
                openDelete={openDelete}
                onClose={() => setOpenDelete(false)}
                onDelete={() => {
                    deleteMutation.mutate(selectedFileName, {
                        onSuccess: () => {
                            setSelectedFileName(null);
                        }
                    });
                }}
                isPending={deleteMutation.isPending}
            />

            <CreateGoodsReceiptDialog
                open={goodsReceiptDialogOpen}
                onClose={handleCloseGoodsReceiptDialog}
                onSubmit={handleGoodsReceiptDialogSubmit}
                invoice={goodsReceiptInvoice}
            />
        </StyledContainer>
    );
}

export default InvoiceTable;